import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  FieldHeader,
  FieldHelpText,
  FIELD_SHELL_CLASS,
  FIELD_BARE_INPUT_CLASS,
} from './FieldParts';
import { cn } from '../lib/utils';
import {
  useElementValue,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

const EmailInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    defaultValue?: string;
    hidden?: boolean;
  };
  const displayValue = useDefaultValue(value, setValue, data?.defaultValue);
  const label = data?.label || 'Email';
  const subLabel = data?.subLabel;
  const placeholder = data?.placeholder || 'Enter email address...';
  const required = data?.required || false;
  const helpText = data?.helpText;

  // Read-only (e.g. a completed result): render the entered email as
  // static text — no input.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          {displayValue ? (
            displayValue
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <div className={cn(FIELD_SHELL_CLASS, 'items-center gap-[10px] px-[var(--forms-input-px)]')}>
        <span className="flex flex-none items-center text-[var(--forms-optional)]">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <polyline points="3 7 12 13 21 7" />
          </svg>
        </span>
        <input
          id={element.id}
          type="email"
          placeholder={placeholder}
          value={displayValue || ''}
          onChange={(e) => setValue(e.target.value)}
          className={FIELD_BARE_INPUT_CLASS}
        />
      </div>
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default EmailInput;
